import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import parentStyles from 'srcReact/WorkplaceSettingsPage/styles.module.scss'
import styles from './styles.module.scss'
import useInfinitScroll from 'srcReact/helpers/hooks/useInfinitScroll'
import { useTranslation } from 'react-i18next'
import SearchTextBox from 'srcReact/components/common2/SearchTextBox'
import OrderByDropdown from 'srcReact/components/common2/OrderByDropdown'
import LightProfileModal from 'srcReact/components/common2/LightProfileModal'
import NotFound from 'srcReact/components/common2/NotFound'
import Avatar from 'srcReact/components/common2/Avatar'
import Spinner from 'srcReact/components/common2/Spinner'
import { workplaceSettingsActions } from 'srcReact/WorkplaceSettingsPage'

const Members = ({ selectedWorkplace }) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const {
    login: { user: loggedInUser },
    workplaceSettings: { workplace_members }
  } = useSelector((state) => state)
  const [searchText, setSearchText] = useState('')
  const [orderby, setOrderby] = useState('firstname')
  const [orderByOptions] = useState([
    { value: 'firstname', label: t('orderBy.name') },
    { value: 'shared_interests', label: t('orderBy.interests') },
    { value: 'ancienty', label: t('orderBy.ancienty') },
    { value: 'lastJoined', label: t('orderBy.lastJoined') }
  ])
  const [selectedOrder, setSelectedOrder] = useState({
    value: 'firstname',
    label: t('orderBy.name')
  })
  const [sharedInterestsCount] = useState(1)
  const [withPhoto] = useState(1)
  const [photoSize] = useState(80)
  const [excludeManagers] = useState(0)
  const [showSearchPopup, setShowSearchPopup] = useState(false)
  const [modalData, setModalData] = useState({
    isShowUserModal: false,
    selectedUserIndex: -1,
    selectedModalUser: {}
  })
  const [pagination, setPagination] = useState({
    nextOffset: 0,
    isPagination: false
  })

  useEffect(() => {
    if (selectedWorkplace?.id > 0) {
      dispatch(
        workplaceSettingsActions.getWorkplaceSettingsMembers(
          selectedWorkplace?.id,
          searchText,
          orderby,
          sharedInterestsCount,
          withPhoto,
          photoSize,
          excludeManagers,
          pagination.nextOffset
        )
      )
    }
  }, [selectedWorkplace, searchText, orderby])

  useEffect(() => {
    if (selectedWorkplace?.id > 0 && pagination.isPagination) {
      dispatch(
        workplaceSettingsActions.getWorkplaceSettingsMembersPagination(
          selectedWorkplace?.id,
          searchText,
          orderby,
          sharedInterestsCount,
          withPhoto,
          photoSize,
          excludeManagers,
          pagination.nextOffset
        )
      )
    }
  }, [pagination])

  const handleSearchFocus = () => {
    setShowSearchPopup(true)
  }
  const handleSearchBlur = () => {
    setShowSearchPopup(false)
  }
  const handleSearchKeyDown = () => {
    setShowSearchPopup(false)
  }
  const handleOrderByChange = (order) => {
    setPagination({ nextOffset: 0, isPagination: false })
    setOrderby(order.value)
    setSelectedOrder(orderByOptions.find((o) => o.value === order.value))
  }
  const handleSearch = (searchValue) => {
    setPagination({ nextOffset: 0, isPagination: false })
    setSearchText(searchValue)
  }
  const lastMemberRef = useInfinitScroll(
    workplace_members?.loading,
    workplace_members?.members_stats?.remaining,
    workplace_members?.members_stats?.next_offset,
    setPagination,
    [workplace_members?.loading]
  )
  const hideUserModal = () => {
    setModalData({
      ...modalData,
      isShowUserModal: false
    })
  }
  const showUserModal = (user, index) => {
    setModalData({
      isShowUserModal: true,
      selectedUserIndex: index,
      selectedModalUser: user
    })
  }
  const navigate = (direction) => {
    const { selectedUserIndex } = modalData
    const nextUserIndex = direction === 'next' ? selectedUserIndex + 1 : selectedUserIndex - 1
    if (nextUserIndex < workplace_members?.members?.length && nextUserIndex >= 0) {
      const navigatedUser = workplace_members?.members[nextUserIndex]
      setModalData({
        ...modalData,
        selectedUserIndex: nextUserIndex,
        selectedModalUser: navigatedUser
      })
    }
  }
  const handleUpdateMemberRole = (userId, role, action) => {
    if (action === 'add') {
      dispatch(workplaceSettingsActions.addWorkplaceMemberRole(selectedWorkplace?.id, userId, role, action))
    } else if (action === 'remove') {
      dispatch(workplaceSettingsActions.removeWorkplaceMemberRole(selectedWorkplace?.id, userId, role, action))
    }
    hideUserModal()
  }
  const handleRemoveMember = (userId) => {
    dispatch(workplaceSettingsActions.removeWorkplaceMember(selectedWorkplace?.id, userId))
    hideUserModal()
  }
  const getOptionsBasedOnRole = () => {
    const { selectedModalUser, selectedUserIndex } = modalData
    let options = []
    if (
      selectedUserIndex > -1 &&
      loggedInUser.id !== selectedModalUser.user_id &&
      selectedWorkplace?.role?.includes('workplace-manager')
    ) {
      if (selectedModalUser.roles.includes('workplace-manager')) {
        options.push({
          text: t('global.removeRole'),
          onClick: () => handleUpdateMemberRole(selectedModalUser.user_id, 'workplace-manager', 'remove')
        })
      } else {
        options.push({
          text: t('global.makeWorkplaceManager'),
          onClick: () => handleUpdateMemberRole(selectedModalUser.user_id, 'workplace-manager', 'add')
        })
        options.push({
          text: t('global.removeFromWorkplace'),
          onClick: () => handleRemoveMember(selectedModalUser.user_id)
        })
      }
    }
    return options
  }
  const isFocused = showSearchPopup || searchText.length > 0
  return (
    <div className={styles.membersWrapper}>
      <div className={styles.header}>
        <SearchTextBox
          id='memberSearch'
          value={searchText}
          {...(searchText.length === 0 && { clear: true })}
          minimumSearchChar={1}
          size='large'
          iconPosition='start'
          placeholder={t('workplaceSettings.members.searchPlaceholder')}
          handleSearch={handleSearch}
          cssClass={`${parentStyles.searchInputContainer} ${isFocused && parentStyles.focused}`}
          iconCSS={`${parentStyles.iconCSS} ${isFocused && parentStyles.focused}`}
          inputCssClass={parentStyles.searchInput}
          buttonCssClass={`${parentStyles.buttonCssClass} ${isFocused && parentStyles.focused}`}
          handleFocus={handleSearchFocus}
          handleBlur={handleSearchBlur}
          handleKeyDown={handleSearchKeyDown}
          style='secondary'
        />
        <OrderByDropdown options={orderByOptions} selected={selectedOrder} handleOrderByChange={handleOrderByChange} />
      </div>

      <div className={styles.members}>
        {!workplace_members?.loading && workplace_members?.loaded && workplace_members?.members?.length === 0 ? (
          <NotFound title={t('global.no_results')} className={styles.notFoundWrapper} />
        ) : (
          workplace_members?.loaded &&
          workplace_members?.members?.length > 0 &&
          workplace_members?.members?.map((member, index) => {
            const isLastMember = workplace_members?.members?.length === index + 1
            return (
              <Avatar
                userId={member.user_id}
                firstname={member.firstname}
                lastname={member.lastname}
                commonInterestsCount={member.shared_interest_count}
                profilePicture={member.profile_picture_url}
                lastMemberRef={isLastMember ? lastMemberRef : null}
                key={'member_' + member.user_id}
                rounded
                onClickAction={() => showUserModal(member, index)}
                showUserName
                showCommonInterests={member.user_id !== loggedInUser.id}
                mimeType={member.mime_type}
                originalTag={member.original_tag}
                isOriginal={member.is_original}
                imgWrapperSize='large84'
                withExpertiseGroup={member.with_expertise_group}
              />
            )
          })
        )}
      </div>
      {modalData.isShowUserModal && (
        <LightProfileModal
          userIdwithRole={modalData.selectedModalUser.user_id}
          show={modalData.isShowUserModal}
          userswithRole={workplace_members?.members}
          userIndexWithRole={modalData.selectedUserIndex}
          roleManagementOptions={getOptionsBasedOnRole()}
          closeModalWithRole={hideUserModal}
          navigateWithRole={navigate}
        />
      )}
      {workplace_members?.loading && <Spinner />}
    </div>
  )
}

export default Members
